import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LazyMarkdown } from '@sorare/core/src/atoms/markdown/LazyMarkdown';
import { Card } from '@sorare/core/src/components/card/Card';
import {
  ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { getSportFromSportOrShared } from '@sorare/core/src/lib/sports';

import { DumbNotification } from 'components/activity/DumbNotification';

import { commonNotificationInterfaceFragment } from '../fragments';
import type { CommonNotificationProps } from '../types';
import type { CardCollectionNotification_cardCollectionNotification } from './__generated__/index.graphql';

type Props = CommonNotificationProps & {
  notification: CardCollectionNotification_cardCollectionNotification;
};

export const CardCollectionNotification = ({
  notification,
  ...rest
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { generateSportContextPath } = useSportContext();
  if (!currentUser) return null;
  const {
    name,
    createdAt,
    anyCard: card,
    cardCollection,
    sport,
    read,
    shieldShopItem,
    markdownBody,
  } = notification;

  if (name === 'card_attached' && card) {
    return (
      <DumbNotification
        name={name}
        title={<LazyMarkdown data={markdownBody} />}
        userAvatar={currentUser}
        link={generateSportContextPath(
          ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTION,
          {
            params: {
              slug: currentUser.slug,
              collection: cardCollection.slug,
            },
            sport: card.sport,
          }
        )}
        cardPicture={<Card card={card} />}
        createdAt={createdAt}
        sport={sport}
        read={read}
        {...rest}
      />
    );
  }

  if (name === 'shield_rewarded') {
    return (
      <DumbNotification
        name={name}
        title={<LazyMarkdown data={markdownBody} />}
        userAvatar={currentUser}
        createdAt={createdAt}
        sport={sport}
        read={read}
        {...rest}
      />
    );
  }

  if (name === 'shield_deprived') {
    const actualSport = getSportFromSportOrShared(sport);
    const equippedClubShieldId = actualSport
      ? currentUser?.anyUserSportProfile?.clubBadge?.id
      : null;
    const collectionShieldId = shieldShopItem?.id;

    return (
      <DumbNotification
        name={name}
        title={
          <Vertical gap={0}>
            <span>
              <LazyMarkdown data={markdownBody} />
            </span>
            {equippedClubShieldId === collectionShieldId && (
              <span>
                <FormattedMessage
                  id="Activity.Notification.equippedShieldDeprived"
                  defaultMessage="You will lose access to your badge once it is unequipped"
                />
              </span>
            )}
          </Vertical>
        }
        userAvatar={currentUser}
        link={generateSportContextPath(
          ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
          {
            params: {
              collectionSlug: cardCollection.slug,
            },
          }
        )}
        createdAt={createdAt}
        sport={sport}
        read={read}
        {...rest}
      />
    );
  }

  return null;
};

CardCollectionNotification.fragments = {
  cardCollectionNotification: gql`
    fragment CardCollectionNotification_cardCollectionNotification on CardCollectionNotification {
      id
      ...Notification_notificationInterface
      cardCollection {
        slug
        name
        team: anyTeam {
          slug
          name
        }
      }
      anyCard {
        slug
        pictureUrl
        sport
        player {
          slug
          displayName
        }
        ...Card_anyCard
      }
      shieldShopItem {
        id
      }
    }
    ${commonNotificationInterfaceFragment}
    ${Card.fragments.anyCard}
  ` as TypedDocumentNode<CardCollectionNotification_cardCollectionNotification>,
};
